import classes from './Dropdown.module.scss';
import './Dropdown.global.scss';
import React, {
  cloneElement,
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  useCallback
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createPopper } from '@popperjs/core';

import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { portalRoot } from './Portal/Portal';

let KEYCODES = {
  ESCAPE: 27
};

let Dropdown = forwardRef((props, forwardedRef) => {
  let {
    trigger,
    content,
    arrow,
    onOpen,
    onClose,
    width,
    className,
    modifiers = [],
    placement,
    allowClickOn
  } = props;
  let [isOpen, setIsOpen] = useState(false);
  let [isHidden, setIsHidden] = useState(false);
  let triggerRef = useRef();
  let popperRef = useRef();
  let popperInstance = useRef();

  let close = useCallback(
    event => {
      setIsOpen(false);
      setIsHidden(false);
      if (typeof onClose === 'function') {
        onClose(event);
      }
    },
    [onClose]
  );

  useOnClickOutside(popperRef, close, {
    allowClickOnPortal: true,
    allowClickOnTooltip: true,
    allowClickOn,
    triggerRef
  });

  useImperativeHandle(
    forwardedRef,
    () => ({
      close,
      hide() {
        setIsHidden(true);
      }
    }),
    [close]
  );

  function onTriggerClick(event) {
    if (isOpen) {
      close(event);
    } else {
      setIsOpen(true);
      if (typeof onOpen === 'function') {
        onOpen(event);
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      popperInstance.current = createPopper(
        triggerRef.current,
        popperRef.current,
        {
          placement: placement,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8]
              }
            },
            ...modifiers
          ]
        }
      );
      return () => {
        popperInstance.current.destroy();
      };
    }
  }, [isOpen, modifiers, placement]);

  useEffect(() => {
    function closeOnEsc(event) {
      if (event.keyCode === KEYCODES.ESCAPE && isOpen) {
        close(event);
      }
    }
    document.addEventListener('keydown', closeOnEsc, true);
    return () => {
      document.removeEventListener('keydown', closeOnEsc, true);
    };
  }, [close, isOpen]);

  useEffect(() => {
    if (window.ResizeObserver && triggerRef.current.nodeType === 1) {
      let ro = new window.ResizeObserver(() => {
        if (popperInstance.current) {
          popperInstance.current.update();
        }
      });
      ro.observe(triggerRef.current);
      return () => {
        ro.disconnect();
      };
    }
  }, []);

  return (
    <>
      {typeof trigger === 'function'
        ? trigger({
            className: classes.trigger,
            ref: triggerRef,
            onClick: onTriggerClick,
            isOpen
          })
        : cloneElement(trigger, {
            ref: triggerRef,
            className: classnames(classes.trigger, trigger.props.className),
            onClick: onTriggerClick
          })}
      {isOpen &&
        createPortal(
          <div
            id="dropdown-tooltip"
            ref={popperRef}
            style={width ? { width } : {}}
            className={classnames(
              classes.popup,
              { [classes.arrow]: arrow, [classes.popupHidden]: isHidden },
              className
            )}
          >
            {content}
            {arrow && <div id="arrow" data-popper-arrow />}
          </div>,
          portalRoot
        )}
    </>
  );
});

Dropdown.propTypes = {
  trigger: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  content: PropTypes.node.isRequired,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  width: PropTypes.string,
  arrow: PropTypes.bool,
  // left means that the left side of a dropdown will be aligned with trigger's left corner
  // right means that the right side of a dropdown will be aligned with trigger's right corner
  placement: PropTypes.oneOf([
    'bottom',
    'bottom-end',
    'bottom-start',
    'top',
    'top-start',
    'top-end',
    'right',
    'right-end',
    'right-start',
    'left',
    'left-end',
    'left-start'
  ]),
  className: PropTypes.string,
  modifiers: PropTypes.array,
  allowClickOn: PropTypes.array
};

Dropdown.defaultProps = {
  arrow: true,
  placement: 'bottom-end'
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
